/**
 * Environment Configuration System
 * 
 * This file centralizes all environment-specific configuration for the application.
 * It supports three environments: development, staging, and production.
 * 
 * 📍 CONFIGURACIÓN DE URLs DE API:
 * 
 * Las URLs de API están configuradas en cada ambiente:
 * 
 * - DEVELOPMENT (línea ~83): 'http://localhost:8000/api/v1'
 * - STAGING (línea ~116): 'https://staging-api.process-optimizer.com/api/v1'
 * - PRODUCTION (línea ~149): 'https://api.process-optimizer.com/api/v1'
 * 
 * Para cambiar las URLs, tienes 3 opciones:
 * 
 * 1. Editar directamente en este archivo (líneas mencionadas arriba)
 * 
 * 2. Usar variables específicas por ambiente en .env:
 *    VITE_TESTING_API_BASE_URL=http://localhost:8000/api/v1
 *    VITE_STG_API_BASE_URL=https://staging-api.example.com/api/v1
 *    VITE_PROD_API_BASE_URL=https://api.example.com/api/v1
 * 
 * 3. Usar variable genérica (aplica a todos los ambientes):
 *    VITE_API_BASE_URL=https://api.example.com/api/v1
 * 
 * Usage:
 * 
 * 1. Import helper functions:
 *    import { getEnvironment, getApiUrl, isProduction, isFeatureEnabled } from '@/config/environments';
 * 
 * 2. Access environment config:
 *    const apiUrl = getApiUrl();
 *    const clerkKey = getEnvironment().clerk.publishableKey;
 * 
 * 3. Check features:
 *    if (isFeatureEnabled('aiQuestions')) {
 *      // Use AI features
 *    }
 * 
 * 4. Environment switching:
 *    Set VITE_APP_ENV=staging npm run dev
 *    Or configure in .env file: VITE_APP_ENV=production
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type AppEnvironment = 'development' | 'staging' | 'production';

export interface Environment {
  name: AppEnvironment;
  api: {
    baseUrl: string;
    timeout: number;
    endpoints: {
      health: string;
      auth: string;
      assessments: string;
      reports: string;
      industries: string;
      documents: string;
      admin: string;
    };
  };
  clerk: {
    publishableKey: string;
  };
  features: {
    aiQuestions: boolean;
    documentUpload: boolean;
    adminDashboard: boolean;
    debugMode: boolean;
    analytics: boolean;
  };
  logging: {
    level: 'debug' | 'info' | 'warn' | 'error';
    enableConsole: boolean;
    enableRemote: boolean;
  };
}

// ============================================================================
// ENVIRONMENT CONFIGURATIONS
// ============================================================================

const development: Environment = {
  name: 'development',
  api: {
    // 🔧 DEVELOPMENT API URL
    // Checks: VITE_TESTING_API_BASE_URL → VITE_API_BASE_URL → default
    baseUrl: getApiUrlForEnv('development', 'http://localhost:8000/api/v1'),
    timeout: 30000,
    endpoints: {
      health: '/health',
      auth: '/auth',
      assessments: '/assessments',
      reports: '/reports',
      industries: '/industries',
      documents: '/documents',
      admin: '/admin',
    },
  },
  clerk: {
    publishableKey: getEnvVar('VITE_CLERK_PUBLISHABLE_KEY', ''),
  },
  features: {
    aiQuestions: true,
    documentUpload: true,
    adminDashboard: true,
    debugMode: true,
    analytics: false,
  },
  logging: {
    level: 'debug',
    enableConsole: true,
    enableRemote: false,
  },
};

const staging: Environment = {
  name: 'staging',
  api: {
    // 🔧 STAGING API URL
    // Checks: VITE_STG_API_BASE_URL → VITE_API_BASE_URL → default
    baseUrl: getApiUrlForEnv('staging', 'https://staging-api.process-optimizer.com/api/v1'),
    timeout: 30000,
    endpoints: {
      health: '/health',
      auth: '/auth',
      assessments: '/assessments',
      reports: '/reports',
      industries: '/industries',
      documents: '/documents',
      admin: '/admin',
    },
  },
  clerk: {
    publishableKey: getEnvVar('VITE_CLERK_PUBLISHABLE_KEY', ''),
  },
  features: {
    aiQuestions: true,
    documentUpload: true,
    adminDashboard: true,
    debugMode: false,
    analytics: true,
  },
  logging: {
    level: 'info',
    enableConsole: true,
    enableRemote: true,
  },
};

const production: Environment = {
  name: 'production',
  api: {
    // 🔧 PRODUCTION API URL
    // Checks: VITE_PROD_API_BASE_URL → VITE_API_BASE_URL → default
    baseUrl: getApiUrlForEnv('production', 'https://api.process-optimizer.com/api/v1'),
    timeout: 30000,
    endpoints: {
      health: '/health',
      auth: '/auth',
      assessments: '/assessments',
      reports: '/reports',
      industries: '/industries',
      documents: '/documents',
      admin: '/admin',
    },
  },
  clerk: {
    publishableKey: getEnvVar('VITE_CLERK_PUBLISHABLE_KEY', ''),
  },
  features: {
    aiQuestions: true,
    documentUpload: true,
    adminDashboard: true,
    debugMode: false,
    analytics: true,
  },
  logging: {
    level: 'warn',
    enableConsole: false,
    enableRemote: true,
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get environment variable with fallback
 * @param key Environment variable key (with VITE_ prefix)
 * @param defaultValue Default value if not found
 * @returns Environment variable value or default
 */
function getEnvVar(key: string, defaultValue: string = ''): string {
  if (typeof import.meta.env === 'undefined') {
    return defaultValue;
  }
  return import.meta.env[key] || defaultValue;
}

/**
 * Get API URL for specific environment
 * Checks for environment-specific variable first, then falls back to default
 * @param envName Environment name
 * @param defaultUrl Default URL if no env var is set
 * @returns API base URL
 */
function getApiUrlForEnv(envName: AppEnvironment, defaultUrl: string): string {
  // Map environment names to variable names
  const envVarMap: Record<AppEnvironment, string[]> = {
    development: ['VITE_TESTING_API_BASE_URL', 'VITE_DEVELOPMENT_API_BASE_URL'],
    staging: ['VITE_STG_API_BASE_URL', 'VITE_STAGING_API_BASE_URL'],
    production: ['VITE_PROD_API_BASE_URL', 'VITE_PRODUCTION_API_BASE_URL']
  };
  
  // Try environment-specific variables first (in order)
  const envVarNames = envVarMap[envName] || [];
  for (const varName of envVarNames) {
    const envSpecificUrl = getEnvVar(varName, '');
    if (envSpecificUrl) {
      return envSpecificUrl;
    }
  }
  
  // Fall back to generic variable
  const genericUrl = getEnvVar('VITE_API_BASE_URL', '');
  if (genericUrl) {
    return genericUrl;
  }
  
  // Finally, use default from code
  return defaultUrl;
}

/**
 * Get current environment name
 * @returns Current environment (development, staging, or production)
 */
export function getCurrentEnvironmentName(): AppEnvironment {
  const env = getEnvVar('VITE_APP_ENV', 'development').toLowerCase();
  
  if (env === 'production' || env === 'prod') {
    return 'production';
  }
  
  if (env === 'staging' || env === 'stage') {
    return 'staging';
  }
  
  return 'development';
}

/**
 * Get current environment configuration
 * @returns Environment configuration object
 */
export function getEnvironment(): Environment {
  const envName = getCurrentEnvironmentName();
  
  switch (envName) {
    case 'production':
      return production;
    case 'staging':
      return staging;
    case 'development':
    default:
      return development;
  }
}

/**
 * Get API base URL
 * @returns Complete API base URL
 */
export function getApiUrl(): string {
  return getEnvironment().api.baseUrl;
}

/**
 * Get API endpoint URL
 * @param endpoint Endpoint name (e.g., 'assessments', 'reports')
 * @returns Complete endpoint URL
 */
export function getApiEndpoint(endpoint: keyof Environment['api']['endpoints']): string {
  const env = getEnvironment();
  return `${env.api.baseUrl}${env.api.endpoints[endpoint]}`;
}

/**
 * Check if current environment is production
 * @returns true if production environment
 */
export function isProduction(): boolean {
  return getCurrentEnvironmentName() === 'production';
}

/**
 * Check if current environment is staging
 * @returns true if staging environment
 */
export function isStaging(): boolean {
  return getCurrentEnvironmentName() === 'staging';
}

/**
 * Check if current environment is development
 * @returns true if development environment
 */
export function isDevelopment(): boolean {
  return getCurrentEnvironmentName() === 'development';
}

/**
 * Check if a feature is enabled in current environment
 * @param feature Feature name
 * @returns true if feature is enabled
 */
export function isFeatureEnabled(feature: keyof Environment['features']): boolean {
  return getEnvironment().features[feature];
}

/**
 * Get Clerk publishable key
 * @returns Clerk publishable key
 */
export function getClerkPublishableKey(): string {
  return getEnvironment().clerk.publishableKey;
}

/**
 * Get API timeout in milliseconds
 * @returns API timeout value
 */
export function getApiTimeout(): number {
  return getEnvironment().api.timeout;
}

/**
 * Get logging configuration
 * @returns Logging configuration
 */
export function getLoggingConfig(): Environment['logging'] {
  return getEnvironment().logging;
}

/**
 * Log message based on environment configuration
 * @param level Log level
 * @param message Message to log
 * @param data Optional data to log
 */
export function log(
  level: 'debug' | 'info' | 'warn' | 'error',
  message: string,
  data?: any
): void {
  const config = getLoggingConfig();
  
  // Check if log level is enabled
  const levels = ['debug', 'info', 'warn', 'error'];
  const currentLevelIndex = levels.indexOf(config.level);
  const messageLevelIndex = levels.indexOf(level);
  
  if (messageLevelIndex < currentLevelIndex) {
    return;
  }
  
  if (config.enableConsole) {
    const logFn = console[level] || console.log;
    if (data) {
      logFn(`[${level.toUpperCase()}] ${message}`, data);
    } else {
      logFn(`[${level.toUpperCase()}] ${message}`);
    }
  }
  
  // Here you could add remote logging if enableRemote is true
  if (config.enableRemote) {
    // TODO: Implement remote logging (e.g., Sentry, LogRocket, etc.)
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

export default getEnvironment;

